import axios from 'axios'
import { emitSessionExpiry } from '../utils/sessionExpiry'

const API_URL = process.env.REACT_APP_API_URL!

// Separate axios instance for admin API calls
export const adminApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Store admin token separately from user token
let cachedAdminToken: string | null = null

// Get cached admin token from sessionStorage on init
if (typeof window !== 'undefined') {
  const stored = sessionStorage.getItem('adminToken')
  if (stored) {
    cachedAdminToken = stored
  }
}

// Add admin token to requests
adminApi.interceptors.request.use(async config => {
  try {
    // Get admin token from sessionStorage if not cached
    if (!cachedAdminToken && typeof window !== 'undefined') {
      cachedAdminToken = sessionStorage.getItem('adminToken')
    }

    if (cachedAdminToken) {
      config.headers.Authorization = `Bearer ${cachedAdminToken}`
    }
  } catch (error) {
  }
  return config
})

// Handle 401 errors by clearing admin token and emitting session expiry
// Note: 403 (permission denied) should NOT log out - user is authenticated but lacks permission
adminApi.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      cachedAdminToken = null
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('adminToken')
        sessionStorage.removeItem('adminRefreshToken')
        sessionStorage.removeItem('adminUser')
      }
      // Emit session expiry event so UI can redirect to admin login
      emitSessionExpiry('admin', error.response?.data?.message || 'Admin session expired')
    }
    // 403 errors are permission denied - don't logout, just reject with the error
    return Promise.reject(error)
  }
)

// Update cached admin token
export const setAdminToken = (token: string) => {
  cachedAdminToken = token
  if (typeof window !== 'undefined') {
    sessionStorage.setItem('adminToken', token)
  }
}

// Clear admin token
export const clearAdminToken = () => {
  cachedAdminToken = null
  if (typeof window !== 'undefined') {
    sessionStorage.removeItem('adminToken')
    sessionStorage.removeItem('adminRefreshToken')
    sessionStorage.removeItem('adminUser')
  }
}

// Admin Auth API
export const adminAuthAPI = {
  login: async (email: string, password: string) => {
    const response = await adminApi.post('/admin/auth/login', { email, password })
    return response.data
  },

  completeNewPassword: async (email: string, newPassword: string, session: string) => {
    const response = await adminApi.post('/admin/auth/complete-new-password', {
      email,
      newPassword,
      session
    })
    return response.data
  },

  refreshToken: async (refreshToken: string) => {
    const response = await adminApi.post('/admin/auth/refresh', { refreshToken })
    return response.data
  },

  changePassword: async (newPassword: string) => {
    const response = await adminApi.post('/admin/auth/change-password', { newPassword })
    return response.data
  },

  resetPassword: async (email: string, newPassword: string) => {
    const response = await adminApi.post('/admin/auth/reset-password', { email, newPassword })
    return response.data
  }
}

// Admin User Management API
export const adminUsersAPI = {
  createUser: async (email: string, temporaryPassword: string, role: string = 'admin') => {
    const response = await adminApi.post('/admin/users', { email, temporaryPassword, role })
    return response.data
  },

  listUsers: async () => {
    const response = await adminApi.get('/admin/users')
    return response.data
  },

  getUser: async (email: string) => {
    const response = await adminApi.get(`/admin/users/${encodeURIComponent(email)}`)
    return response.data
  },

  deleteUser: async (email: string) => {
    const response = await adminApi.delete(`/admin/users/${encodeURIComponent(email)}`)
    return response.data
  },

  updateUser: async (email: string, role: string) => {
    const response = await adminApi.patch(`/admin/users/${encodeURIComponent(email)}`, { role })
    return response.data
  }
}

// Admin Customer Management API
export const adminCustomersAPI = {
  listCustomers: async () => {
    const response = await adminApi.get('/admin/customers')
    return response.data
  },

  getCustomer: async (userId: string) => {
    const response = await adminApi.get(`/admin/customers/${userId}`)
    return response.data
  },

  addCredits: async (userId: string, credits: number, reason?: string) => {
    const response = await adminApi.post(`/admin/customers/${userId}/credits`, {
      credits,
      reason
    })
    return response.data
  },

  updateTier: async (userId: string, tier: string) => {
    const response = await adminApi.patch(`/admin/customers/${userId}/tier`, { tier })
    return response.data
  },

  updateStatus: async (userId: string, enabled: boolean) => {
    const response = await adminApi.patch(`/admin/customers/${userId}/status`, { enabled })
    return response.data
  },

  deleteCustomer: async (userId: string) => {
    const response = await adminApi.delete(`/admin/customers/${userId}`)
    return response.data
  },

  resetPassword: async (userId: string) => {
    const response = await adminApi.post(`/admin/customers/${userId}/reset-password`)
    return response.data
  }
}

// Notification Templates API (admin only)
export const templatesAPI = {
  // Get all templates
  getAll: async (status?: 'active' | 'draft' | 'archived') => {
    const params = status ? `?status=${status}` : ''
    const response = await adminApi.get(`/notifications/templates${params}`)
    return response.data
  },

  // Get single template
  get: async (templateId: string) => {
    const response = await adminApi.get(`/notifications/templates/${templateId}`)
    return response.data
  },

  // Create template
  create: async (template: any) => {
    const response = await adminApi.post('/notifications/templates', template)
    return response.data
  },

  // Update template
  update: async (templateId: string, updates: any) => {
    const response = await adminApi.put(`/notifications/templates/${templateId}`, updates)
    return response.data
  },

  // Delete template
  delete: async (templateId: string) => {
    const response = await adminApi.delete(`/notifications/templates/${templateId}`)
    return response.data
  },

  // Get available placeholders
  getPlaceholders: async () => {
    const response = await adminApi.get('/notifications/templates/placeholders')
    return response.data
  }
}

// Notification Triggers API (admin only)
export const triggersAPI = {
  // Get all triggers
  getAll: async (status?: 'active' | 'paused' | 'archived') => {
    const params = status ? `?status=${status}` : ''
    const response = await adminApi.get(`/notifications/triggers${params}`)
    return response.data
  },

  // Get single trigger
  get: async (triggerId: string) => {
    const response = await adminApi.get(`/notifications/triggers/${triggerId}`)
    return response.data
  },

  // Create trigger
  create: async (trigger: any) => {
    const response = await adminApi.post('/notifications/triggers', trigger)
    return response.data
  },

  // Update trigger
  update: async (triggerId: string, updates: any) => {
    const response = await adminApi.put(`/notifications/triggers/${triggerId}`, updates)
    return response.data
  },

  // Delete trigger
  delete: async (triggerId: string) => {
    const response = await adminApi.delete(`/notifications/triggers/${triggerId}`)
    return response.data
  },

  // Test trigger for a specific user
  test: async (triggerId: string, userId: string) => {
    const response = await adminApi.post(`/notifications/triggers/${triggerId}/test`, { userId })
    return response.data
  }
}

// Admin Notifications API (for sending notifications)
export const adminNotificationsAPI = {
  // Send notification (admin only)
  send: async (request: any) => {
    const response = await adminApi.post('/notifications', request)
    return response.data
  }
}

// Admin Feedback API
export const adminFeedbackAPI = {
  // Get all feedback with filters
  getAll: async (filters?: {
    type?: 'feedback' | 'feature' | 'bug'
    status?: 'new' | 'in_progress' | 'done' | 'wont_do'
    priority?: 'low' | 'medium' | 'high' | 'critical'
    flagged?: boolean
    archived?: boolean
    groupId?: string
  }) => {
    const params = new URLSearchParams()
    if (filters?.type) params.append('type', filters.type)
    if (filters?.status) params.append('status', filters.status)
    if (filters?.priority) params.append('priority', filters.priority)
    if (filters?.flagged !== undefined) params.append('flagged', String(filters.flagged))
    if (filters?.archived !== undefined) params.append('archived', String(filters.archived))
    if (filters?.groupId) params.append('groupId', filters.groupId)

    const response = await adminApi.get(`/admin/feedback${params.toString() ? `?${params.toString()}` : ''}`)
    return response.data
  },

  // Update feedback
  update: async (feedbackId: string, updates: {
    status?: 'new' | 'in_progress' | 'done' | 'wont_do'
    priority?: 'low' | 'medium' | 'high' | 'critical'
    flagged?: boolean
    groupId?: string | null
    archived?: boolean
    assignedTo?: string
    notes?: string
  }) => {
    const response = await adminApi.patch(`/admin/feedback/${feedbackId}`, updates)
    return response.data
  },

  // Group feedbacks
  group: async (feedbackIds: string[], groupName: string) => {
    const response = await adminApi.post('/admin/feedback/group', { feedbackIds, groupName })
    return response.data
  },

  // Delete (archive) feedback
  delete: async (feedbackId: string) => {
    const response = await adminApi.delete(`/admin/feedback/${feedbackId}`)
    return response.data
  }
}

// Admin Beta Referrals API
export const adminAPI = {
  // Get all beta referrals
  getBetaReferrals: async () => {
    const response = await adminApi.get('/admin/referrals/beta')
    return response.data
  },

  // Update beta referral status
  updateBetaReferralStatus: async (invitationId: string, status: 'pending' | 'selected' | 'rejected') => {
    const response = await adminApi.put(`/admin/referrals/beta/${invitationId}`, { status })
    return response.data
  }
}

// Pipeline trigger configuration types
export interface PipelineTriggerConfig {
  location: string
  source: 'rightmove' | 'zoopla'
  maxResults?: number
  strategy?: 'btl' | 'flip' | 'hmo' | 'brrr' | 'rent_to_rent' | 'bmv' | 'r2sa'
  searchTerms?: string[]
}

export interface PipelineTriggerResult {
  location: string
  source: string
  maxResults?: number
  strategy?: string
  runId?: string
  startedAt?: string
  error?: string
}

export interface PipelineTriggerResponse {
  message: string
  triggered: number
  failed: number
  results: PipelineTriggerResult[]
}

// Pipeline config types
export interface PipelineConfigItem {
  configId: string
  location: string
  source: 'rightmove' | 'zoopla'
  maxResults: number
  strategies: string[]
  searchTerms?: string[]
  enabled: boolean
  createdAt: string
  updatedAt?: string
}

export interface PipelineSchedule {
  name: string
  scheduleExpression: string
  state: 'ENABLED' | 'DISABLED'
  description?: string
}

// Strategy filter settings
export interface StrategyFilterSettings {
  btl: {
    minGrossYield: number
    minNetYield: number
    maxPrice: number
    minCashflow: number
  }
  flip: {
    minDiscountPct: number
    minProfitMargin: number
    minCashProfit: number
    maxPrice: number
  }
  hmo: {
    minBedrooms: number
    maxPrice: number
    minRoomYield: number
    minHmoPremium: number
  }
  brrr: {
    minGrossYield: number
    maxPrice: number
    minEquityReleasePct: number
    minPostRefurbEquity: number
  }
  rentToRent: {
    minBedrooms: number
    minArbitrageMargin: number
    minProfitMarginPct: number
    minRoi: number
  }
}

export interface StrategyFiltersResponse {
  settings: StrategyFilterSettings
  defaults: StrategyFilterSettings
  updatedAt: string | null
  updatedBy: string | null
}

// Admin Pipeline API
export const adminPipelineAPI = {
  // Trigger pipeline with custom configuration
  trigger: async (config: PipelineTriggerConfig): Promise<PipelineTriggerResponse> => {
    const response = await adminApi.post('/admin/pipeline/trigger', {
      customConfig: config
    })
    return response.data
  },

  // Trigger pipeline using stored configurations (optionally filtered by locations)
  triggerFromConfig: async (locations?: string[]): Promise<PipelineTriggerResponse> => {
    const response = await adminApi.post('/admin/pipeline/trigger', {
      locations
    })
    return response.data
  },

  // Get all pipeline configurations
  getConfigs: async (): Promise<{ configs: PipelineConfigItem[] }> => {
    const response = await adminApi.get('/admin/pipeline/configs')
    return response.data
  },

  // Create a new pipeline configuration
  createConfig: async (config: Omit<PipelineConfigItem, 'configId' | 'createdAt' | 'updatedAt'>): Promise<{ config: PipelineConfigItem }> => {
    const response = await adminApi.post('/admin/pipeline/configs', config)
    return response.data
  },

  // Update a pipeline configuration
  updateConfig: async (configId: string, updates: Partial<PipelineConfigItem>): Promise<{ config: PipelineConfigItem }> => {
    const response = await adminApi.put(`/admin/pipeline/configs/${encodeURIComponent(configId)}`, updates)
    return response.data
  },

  // Delete a pipeline configuration
  deleteConfig: async (configId: string): Promise<void> => {
    await adminApi.delete(`/admin/pipeline/configs/${encodeURIComponent(configId)}`)
  },

  // Get pipeline schedules
  getSchedules: async (): Promise<{ schedules: PipelineSchedule[] }> => {
    const response = await adminApi.get('/admin/pipeline/schedule')
    return response.data
  },

  // Update a pipeline schedule
  updateSchedule: async (ruleName: string, updates: { scheduleExpression?: string, enabled?: boolean, description?: string }): Promise<void> => {
    await adminApi.put('/admin/pipeline/schedule', { ruleName, ...updates })
  },

  // Get strategy filter settings
  getStrategyFilters: async (): Promise<StrategyFiltersResponse> => {
    const response = await adminApi.get('/admin/pipeline/strategy-filters')
    return response.data
  },

  // Update strategy filter settings
  updateStrategyFilters: async (settings: StrategyFilterSettings): Promise<{ message: string; settings: StrategyFilterSettings; updatedAt: string; updatedBy: string }> => {
    const response = await adminApi.put('/admin/pipeline/strategy-filters', { settings })
    return response.data
  }
}

// ===========================================================================
// Social Posts API
// ===========================================================================

export const adminSocialPostsAPI = {
  listPosts: async (filters?: { status?: string; platform?: string; strategy?: string }) => {
    const response = await adminApi.get('/admin/social-posts', { params: filters })
    return response.data
  },

  getPost: async (postId: string) => {
    const response = await adminApi.get(`/admin/social-posts/${postId}`)
    return response.data
  },

  approvePost: async (postId: string) => {
    const response = await adminApi.post(`/admin/social-posts/${postId}/approve`)
    return response.data
  },

  rejectPost: async (postId: string, note?: string) => {
    const response = await adminApi.post(`/admin/social-posts/${postId}/reject`, { note })
    return response.data
  },

  retryPost: async (postId: string) => {
    const response = await adminApi.post(`/admin/social-posts/${postId}/retry`)
    return response.data
  },

  getConfig: async () => {
    const response = await adminApi.get('/admin/social-posts/config')
    return response.data
  },

  getCredentialStatus: async () => {
    const response = await adminApi.get('/admin/social-posts/credentials')
    return response.data
  },

  updateConfig: async (updates: Record<string, any>) => {
    const response = await adminApi.put('/admin/social-posts/config', updates)
    return response.data
  },
}

// Admin Knowledge Hub API
export const adminKnowledgeHubAPI = {
  // Categories
  listCategories: async () => {
    const response = await adminApi.get('/admin/knowledge-hub/categories')
    return response.data
  },

  createCategory: async (data: { name: string; description?: string; icon?: string; sortOrder?: number; publicVisible?: boolean }) => {
    const response = await adminApi.post('/admin/knowledge-hub/categories', data)
    return response.data
  },

  updateCategory: async (categoryId: string, updates: Record<string, any>) => {
    const response = await adminApi.put(`/admin/knowledge-hub/categories/${categoryId}`, updates)
    return response.data
  },

  // Articles
  listArticles: async (categoryId: string, params?: { status?: string; limit?: number; cursor?: string }) => {
    const response = await adminApi.get('/admin/knowledge-hub/articles', {
      params: { categoryId, ...params }
    })
    return response.data
  },

  getArticle: async (articleId: string) => {
    const response = await adminApi.get(`/admin/knowledge-hub/articles/${articleId}`)
    return response.data
  },

  createArticle: async (data: {
    title: string
    content: string
    excerpt?: string
    categoryId: string
    contentRiskLevel?: string
    tags?: string[]
    isPinned?: boolean
  }) => {
    const response = await adminApi.post('/admin/knowledge-hub/articles', data)
    return response.data
  },

  updateArticle: async (articleId: string, updates: Record<string, any>) => {
    const response = await adminApi.put(`/admin/knowledge-hub/articles/${articleId}`, updates)
    return response.data
  },

  deleteArticle: async (articleId: string) => {
    const response = await adminApi.delete(`/admin/knowledge-hub/articles/${articleId}`)
    return response.data
  },

  // Phase 2: Threads & Moderation
  listThreads: async (params?: { categoryId?: string; status?: string; limit?: number; cursor?: string }) => {
    const response = await adminApi.get('/admin/knowledge-hub/threads', { params })
    return response.data
  },

  getThread: async (threadId: string) => {
    const response = await adminApi.get(`/admin/knowledge-hub/threads/${threadId}`)
    return response.data
  },

  deleteThread: async (threadId: string) => {
    const response = await adminApi.delete(`/admin/knowledge-hub/threads/${threadId}`)
    return response.data
  },

  updateThread: async (threadId: string, updates: { status?: string; isPinned?: boolean }) => {
    const response = await adminApi.put(`/admin/knowledge-hub/threads/${threadId}`, updates)
    return response.data
  },

  adminReply: async (threadId: string, body: string) => {
    const response = await adminApi.post(`/admin/knowledge-hub/threads/${threadId}/replies`, { body })
    return response.data
  },

  acceptAnswer: async (threadId: string, replyId: string) => {
    const response = await adminApi.put(`/admin/knowledge-hub/threads/${threadId}/accept/${replyId}`)
    return response.data
  },

  listFlags: async (params?: { limit?: number; cursor?: string }) => {
    const response = await adminApi.get('/admin/knowledge-hub/flags', { params })
    return response.data
  },

  resolveFlag: async (flagId: string, resolution: string) => {
    const response = await adminApi.put(`/admin/knowledge-hub/flags/${flagId}`, { resolution })
    return response.data
  },
}
